import React, { useMemo, useState } from 'react';
// Note: Group view is not implemented in legacy version. So this is commented out.
// import GroupsSelection from './components/GroupsSelection/GroupsSelection';
// import GroupsSelectionSummary from './components/GroupsSelectionSummary/GroupsSelectionSummary';
import {
  STUDENTS_VIEW,
  NavigationView as NavigationViewType,
} from './components/Navigation/constants';
import { SpaceUser, SpaceGroup } from './Conversion.types';
import { CREATING_EXTENSION_SERVER_ERROR } from '../../Assignments/constants';
import {
  Box,
  Alert,
  Icons,
  InputBase,
  useTheme,
} from '@ublend-npm/aulaui-core';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import StudentRow from '../components/StudentRow';
import {
  ADD_TO_EXTENSION_TOOLTIP,
  REMOVE_FROM_EXTENSION_TOOLTIP,
} from './constants';
import { createStyles } from './NavigationView.styles';

const errorText = CREATING_EXTENSION_SERVER_ERROR;
const USER_ACTION_DISABLED = 'Student submission exists in this assignment.';
const SEARCH_RESULTS_LIMIT = 10;

const DISABLED_ADD_TOOLTIP = (
  <>
    <div>This student can&#39;t be added because they already</div>
    <div>have a submission in the destination assignment</div>
  </>
);

const DISABLED_ADD_ARIA_LABEL =
  "This student can't be added because they already have a submission in the destination assignment";

const filterOutSelectedStudentsFromUnassigned = (
  unassignedStudents: SpaceUser[],
  assignedStudents: SpaceUser[]
) =>
  unassignedStudents.reduce<SpaceUser[]>((acc, user) => {
    if (assignedStudents.some((student) => student.id === user.id)) {
      return acc;
    }
    return [...acc, user];
  }, []);

type NavigationViewProps = {
  spaceGroups: SpaceGroup[];
  data: {
    assignedStudents: SpaceUser[];
    unassignedStudents: SpaceUser[];
    groups: any;
  };
  setData: (newProps: any) => void;
  navigationView: NavigationViewType;
  hasError?: boolean;
  isLoadingStudents?: boolean;
  greyedOutUsers?: string[];
};

const NavigationView = ({
  data: { assignedStudents, groups, unassignedStudents },
  setData,
  spaceGroups,
  navigationView,
  hasError = false,
  greyedOutUsers = [],
  isLoadingStudents = false,
}: NavigationViewProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [search, setSearch] = useState('');
  const isSearchActive = search.trim().length > 0;

  // Show search results based on the search query and the unassigned students
  const searchResults = useMemo(() => {
    if (!isSearchActive) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return filterOutSelectedStudentsFromUnassigned(
      unassignedStudents,
      assignedStudents
    )
      .filter((user) => (user.fullName || '').toLowerCase().includes(query))
      .slice(0, SEARCH_RESULTS_LIMIT);
  }, [isSearchActive, search, unassignedStudents, assignedStudents]);

  // Handle assign student action
  const handleAssignStudent = (student: SpaceUser) => {
    if (greyedOutUsers.includes(student.id)) {
      return;
    }

    const selectedStudents = [...assignedStudents, student];
    setData({
      assignedStudents: selectedStudents,
      unassignedStudents: filterOutSelectedStudentsFromUnassigned(
        unassignedStudents,
        selectedStudents
      ),
    });
    setSearch('');
  };

  // Update the assigned students list by removing the student from the assigned students list
  const handleUnassignStudent = (studentToUnassign: SpaceUser) => {
    const { id } = studentToUnassign;
    const isStudentUnassigned = unassignedStudents.some(
      (student) => student.id === id
    );

    setData({
      unassignedStudents: isStudentUnassigned
        ? unassignedStudents
        : [...unassignedStudents, studentToUnassign],
      assignedStudents: assignedStudents.filter((student) => student.id !== id),
    });
  };

  // Show no result alert if there are no search results and the search is active
  const showNoResultAlert =
    isSearchActive && !isLoadingStudents && searchResults?.length === 0;
  // Show assigned students list if there are assigned students and no no result alert
  const showAssignedStudentsList =
    !showNoResultAlert && assignedStudents?.length > 0 && !isSearchActive;

  return (
    <>
      {navigationView === STUDENTS_VIEW && (
        <Box sx={styles.root}>
          <InputBase
            fullWidth
            disabled={isLoadingStudents}
            placeholder="Search for students"
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(event.target.value)
            }
            startAdornment={<Icons.Search sx={styles.searchIcon} />}
            sx={styles.searchInput}
            inputProps={{
              'aria-label': 'Search for students',
              role: 'searchbox',
              id: 'search-bar',
            }}
          />

          <Box component="span" role="status" sx={styles.visuallyHidden}>
            {isSearchActive && !isLoadingStudents
              ? `${searchResults.length} ${
                  searchResults.length === 1 ? 'student' : 'students'
                } found`
              : ''}
          </Box>

          {isSearchActive && searchResults.length > 0 && (
            <Box
              component="ul"
              role="list"
              aria-label="Student search results"
              sx={styles.searchResults}
            >
              {searchResults?.map((user) => {
                const disabled = greyedOutUsers.includes(user.id);

                return (
                  <StudentRow
                    key={user.id}
                    student={{
                      id: user.id,
                      fullName: user.fullName,
                      avatar: user.avatar,
                      email: user.email,
                    }}
                    isSelected={false}
                    canAdd
                    isDimmed={disabled}
                    isDisabled={disabled}
                    disabledText={disabled ? DISABLED_ADD_TOOLTIP : undefined}
                    disabledAriaLabel={DISABLED_ADD_ARIA_LABEL}
                    showAction={!disabled}
                    actionIcon={Icons.AddCircle}
                    actionTooltip={ADD_TO_EXTENSION_TOOLTIP}
                    showAddedLabel={false}
                    component="div"
                    onActionClick={
                      !disabled ? () => handleAssignStudent(user) : undefined
                    }
                    sx={styles.searchResultRow}
                  />
                );
              })}
            </Box>
          )}

          {showNoResultAlert && (
            <NoResultsCard
              title="No student found!"
              description="Remember to check the spelling."
            />
          )}

          {/* Error alert */}
          {hasError && (
            <Box sx={{ mt: 1, flexShrink: 0 }}>
              <Alert severity="error">{errorText}</Alert>
            </Box>
          )}

          {showAssignedStudentsList && (
            <Box
              component="ul"
              role="list"
              aria-label="Students added to this extension"
              sx={styles.assignedStudentsList}
            >
              {assignedStudents?.map((user) => {
                const { id, fullName, avatar, email, userActionDisabled } =
                  user;
                return (
                  <StudentRow
                    key={id}
                    student={{
                      id,
                      fullName,
                      avatar,
                      email,
                    }}
                    isSelected={false}
                    isDimmed={Boolean(userActionDisabled)}
                    isDisabled={Boolean(userActionDisabled)}
                    disabledText={
                      userActionDisabled ? USER_ACTION_DISABLED : undefined
                    }
                    canAdd={false}
                    showAction={!userActionDisabled}
                    actionIcon={Icons.RemoveCircle}
                    actionTooltip={REMOVE_FROM_EXTENSION_TOOLTIP}
                    showAddedLabel={false}
                    component="div"
                    onActionClick={
                      !userActionDisabled
                        ? () => handleUnassignStudent(user)
                        : undefined
                    }
                    sx={styles.assignedStudentRow}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}
      {/* Note: Group view is not implemented in legacy version. So this is commented out. */}
      {/* {navigationView === GROUP_VIEW && (
        <>
          <GroupsSelection
            selectedGroups={groups}
            spaceGroups={spaceGroups}
            onChange={(e) =>
              setData({
                groups: e.target.value,
              })
            }
          />
          <GroupsSelectionSummary students={groups.students} />
        </>
      )} */}
    </>
  );
};

export default React.memo(NavigationView);
